import type { Tour } from "@/types/tour";

export type TourTermState = {
  date: string;
  available: boolean;
};

/**
 * Kirby toggles arrive as the strings "true"/"false", and a field the office
 * never touched arrives as "" — which means "default: true" in the blueprint,
 * so an empty value counts as available.
 *
 * This is the opt-OUT half of the pair: use it for a switch the blueprint
 * defaults to on (`available`, `singleRoomAvailable`), where "" has to keep
 * meaning "on" or every entry saved before the field existed silently loses it.
 * For an opt-IN switch — one that is off until somebody turns it on — use
 * `isToggledOn` below.
 */
export function normalizeAvailable(
  value: string | boolean | null | undefined,
): boolean {
  if (value === "" || value === null || value === undefined) return true;

  if (typeof value === "string") {
    const val = value.trim().toLowerCase();
    if (val === "false" || val === "0" || val === "no") return false;
    if (val === "true" || val === "1" || val === "yes") return true;
  }

  return Boolean(value);
}

/**
 * The opt-IN half: "" is **false** here. Use it for a switch the blueprint
 * leaves off by default (`banner_toggle`, `visaRequired`), where an untouched
 * field genuinely means the office never asked for it.
 */
export function isToggledOn(
  value: string | boolean | null | undefined,
): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return false;
  const val = value.trim().toLowerCase();
  return val === "true" || val === "1" || val === "yes";
}

/**
 * Every term the tour page actually offers: the legacy `dates`/`dates_toggle`
 * pair — but only while `dates` still holds a value, exactly like the detail
 * page, which renders that card only then — plus each row of the "Termíny
 * výletov" table.
 */
export function getTourTerms(tour: Tour): TourTermState[] {
  const terms: TourTermState[] = [];

  const legacyDate = typeof tour.dates === "string" ? tour.dates.trim() : "";
  if (legacyDate !== "") {
    terms.push({
      date: legacyDate,
      available: normalizeAvailable(tour.dates_toggle),
    });
  }

  for (const term of tour.terms ?? []) {
    const date = typeof term.date === "string" ? term.date.trim() : "";
    if (date === "") continue;
    terms.push({ date, available: normalizeAvailable(term.available) });
  }

  return terms;
}

/**
 * "Obsadené" on a tour card speaks for the whole tour, so it needs every term
 * sold out. The legacy `dates_toggle` alone is not that answer: the office
 * empties the legacy fields once a tour moves to the terms table, leaving a
 * stale toggle that has no bookable date behind it.
 *
 * A tour with no terms at all is never called sold out — there is nothing to
 * be sold out yet, and the card keeps whatever the office wrote on it.
 */
export function isTourSoldOut(tour: Tour): boolean {
  const terms = getTourTerms(tour);
  return terms.length > 0 && terms.every((term) => !term.available);
}
